// GenderSelector.jsx
import React, { useState, useEffect } from 'react';
import './GenderSelector.scss';

const GenderSelector = ({ 
  value = null, 
  onChange = () => {}, 
  disabled = false,
  size = 'medium', // small, medium, large
  theme = 'default' // default, primary, success, danger, dark
}) => {
  const [selectedGender, setSelectedGender] = useState(null);

  const genderOptions = [
    { key: 'M', label: 'Male', fullLabel: 'Male' },
    { key: 'F', label: 'Female', fullLabel: 'Female' },
    { key: 'O', label: 'Other', fullLabel: 'Other' }
  ];

  // Normalize input value to key format
  const normalizeValue = (inputValue) => {
    if (!inputValue) return null;
    
    const normalizedInput = String(inputValue).toLowerCase();
    
    // Handle different input formats
    switch(normalizedInput) {
      case 'm':
      case 'male':
        return 'M';
      case 'f':
      case 'female':
        return 'F';
      case 'o':
      case 'other':
        return 'O';
      default:
        return null;
    }
  };

  // Update internal state when value prop changes
  useEffect(() => {
    const normalizedValue = normalizeValue(value);
    setSelectedGender(normalizedValue);
  }, [value]);

  const handleGenderSelect = (genderKey) => {
    if (disabled) return;
    
    // Toggle functionality: if same gender is clicked, unselect it
    const newSelectedGender = selectedGender === genderKey ? null : genderKey;
    setSelectedGender(newSelectedGender);
    
    if (newSelectedGender === null) {
      // Return null when unselected
      onChange(null);
    } else {
      // Return the same format as input or the key
      const selectedOption = genderOptions.find(option => option.key === newSelectedGender);
      
      // If original value was full name, return full name, otherwise return key
      if (value && typeof value === 'string') {
        const originalFormat = String(value).toLowerCase();
        if (['male', 'female', 'other'].includes(originalFormat)) {
          onChange(selectedOption.fullLabel);
        } else {
          onChange(newSelectedGender);
        }
      } else {
        onChange(newSelectedGender);
      }
    }
  };

  const getSizeClasses = () => {
    switch(size) {
      case 'small':
        return {
          container: 'gender-selector-sm',
          button: 'gender-btn-sm'
        };
      case 'large':
        return {
          container: 'gender-selector-lg',
          button: 'gender-btn-lg'
        };
      default:
        return {
          container: 'gender-selector-md',
          button: 'gender-btn-md'
        };
    }
  };

  const sizeClasses = getSizeClasses();
  const themeClass = theme !== 'default' ? theme : '';

  return (
    <div className={`gender-selector ${sizeClasses.container} ${themeClass} ${disabled ? 'disabled' : ''}`}>
      {genderOptions.map((option) => (
        <button
          key={option.key}
          type="button"
          className={`gender-btn ${sizeClasses.button} ${
            selectedGender === option.key ? 'selected' : ''
          } ${disabled ? 'disabled' : ''}`}
          onClick={() => handleGenderSelect(option.key)}
          disabled={disabled}
          title={option.fullLabel}
          aria-label={option.fullLabel}
          aria-pressed={selectedGender === option.key}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default GenderSelector;